import { AddressLink } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import { Abi } from 'viem'

import abiVestingV2 from 'abi/abi-vesting-v2.json'
import { ContractStatusEnum } from 'constants/index'
import { publicWallet } from 'libs/viem/config'
import { logError } from 'utils/logger'

interface VestingV2Data {
  tokenAddress: string
  firstRelease: number | null
  cliffDuration: number | null
  totalPeriods: number | null
  timePerPeriod: number | null
}

export const useVestingV2 = ({
  contract,
  contractAddress,
}: {
  contract: any
  contractAddress: AddressLink
}) => {
  const [loading, setLoading] = useState(false)

  const [defaultData, setDefaultData] = useState<VestingV2Data>({
    tokenAddress: '',
    firstRelease: null,
    cliffDuration: null,
    totalPeriods: null,
    timePerPeriod: null,
  })

  const getDataContract = useCallback(async () => {
    setLoading(true)
    try {
      if (contract && contractAddress) {
        // Call getVestingParams() and token() together using multicall
        const contracts = [
          {
            address: contractAddress,
            abi: abiVestingV2 as Abi,
            functionName: 'getVestingParams',
          },
          {
            address: contractAddress,
            abi: abiVestingV2 as Abi,
            functionName: 'token',
          },
        ]

        const results = await publicWallet.multicall({
          contracts: contracts,
        })

        if (results.length >= 2) {
          // Process getVestingParams result
          const paramsResult = results[0]
          if (paramsResult.status === ContractStatusEnum.SUCCESS && paramsResult.result) {
            const [firstReleaseRaw, cliffRaw, totalPeriodsRaw, timePerPeriodRaw] =
              paramsResult.result as [bigint, bigint, bigint, bigint]

            // Convert firstRelease from basis points (10000 = 100%) to percentage
            const firstRelease = Number(firstReleaseRaw) / 100

            // Convert cliff from seconds to days
            const cliffDuration = Number(cliffRaw) / 86400

            // Convert timePerPeriod from seconds to days
            const timePerPeriod = Number(timePerPeriodRaw) / 86400

            setDefaultData((prevData) => ({
              ...prevData,
              firstRelease,
              cliffDuration,
              totalPeriods: Number(totalPeriodsRaw),
              timePerPeriod,
            }))
          }

          // Process token() result
          const tokenResult = results[1]
          if (tokenResult.status === ContractStatusEnum.SUCCESS && tokenResult.result) {
            setDefaultData((prevData) => ({
              ...prevData,
              tokenAddress: tokenResult.result as string,
            }))
          }
        }
      }
    } catch (error) {
      logError('Error get data contract V2', error)
    } finally {
      setLoading(false)
    }
  }, [contract, contractAddress])

  useEffect(() => {
    getDataContract()
  }, [contract, getDataContract])

  return {
    ...defaultData,
    loading,
    getDataContract,
  }
}
